import { useApolloClient } from '@apollo/client'
import { saveAs } from 'file-saver'
import {
  MarketOption,
  useProjectMarkets
} from '../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { buildSurveyDoc } from '../utils/surveyDocBuilder/buildSurveyDoc'
import { loadSurveyDocData } from '../utils/surveyDocBuilder/loadSurveyDocData'
import { NamedFile, packDocsZip } from '../utils/surveyDocBuilder/packDocsZip'
import { useOptionalWaveId } from './useOptionalWaveId'
import { useProjectId } from './useProjectId'
import { useSurveyId } from './useSurveyId'

/** One document to export: a market's doc (else the master), in a language. */
export interface ExportTarget {
  market?: MarketOption
  // Omitted for English (master) text.
  languageCode?: string
  // Whether the doc lists screeners; the loader's project-type default if
  // omitted.
  includeAudienceQuestions?: boolean
  // Overrides the name `buildSurveyDoc` gives the file (e.g. the master's
  // "<project> - Master.docx" inside the Download all zip).
  filename?: string
}

export interface ExportDocumentsOptions {
  // Name of the zip saved when there is more than one target.
  zipName: string
  // Called before each document is built: (1-based index, total).
  onProgress?: (current: number, total: number) => void
}

const useDownloadSurveyDoc = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  const client = useApolloClient()

  // The project's markets (base market first, then alphabetical), resolved via
  // the same hook the on-screen visibility indicators use so the exported doc's
  // "Hidden in: …" annotations and market list stay consistent with them. Used
  // for rendering only — the multi-market decision is taken from the freshly
  // queried project type in `loadSurveyDocData`, not from this render-time list.
  const allMarkets = useProjectMarkets()

  return {
    exportSurvey: async (
      languageCode?: string,
      options?: { includeAudienceQuestions?: boolean }
    ) => {
      const params = await loadSurveyDocData(client, {
        projectId,
        surveyId,
        waveId,
        languageCode,
        allMarkets,
        includeAudienceQuestions: options?.includeAudienceQuestions
      })
      const { blob, filename } = await buildSurveyDoc(params)
      saveAs(blob, filename)
    },
    /**
     * Builds each target one after another (each loads its own data) and
     * saves a single .docx, or one zip of them all. Strict: any failed load
     * rejects and nothing is saved, so a caller can show the error rather
     * than ship an empty doc.
     */
    exportDocuments: async (
      targets: ExportTarget[],
      { zipName, onProgress }: ExportDocumentsOptions
    ) => {
      const files: NamedFile[] = []
      for (const [
        index,
        { market, languageCode, includeAudienceQuestions, filename }
      ] of targets.entries()) {
        onProgress?.(index + 1, targets.length)
        // Sequential on purpose: one doc's queries at a time.
        // eslint-disable-next-line no-await-in-loop
        const params = await loadSurveyDocData(client, {
          projectId,
          surveyId,
          waveId,
          languageCode,
          allMarkets,
          market,
          includeAudienceQuestions,
          throwOnError: true
        })
        // eslint-disable-next-line no-await-in-loop
        const file = await buildSurveyDoc(params)
        files.push(filename ? { ...file, filename } : file)
      }
      if (files.length === 1) {
        saveAs(files[0].blob, files[0].filename)
      } else if (files.length > 1) {
        saveAs(await packDocsZip(files), zipName)
      }
    }
  }
}

export default useDownloadSurveyDoc
